import { action, observable, makeObservable, computed } from 'mobx';
import {
	lookupRouteName,
	orderLineStatuses,
	orderStatus,
	shipmentDocumentTypeEnum,
	sourceOfChange,
} from 'common/constants';
import { ApplicationStore } from 'platform/application/stores';
import { OrderStore, ShipmentDocumentStore } from 'modules/order/stores';
import { LookupStore } from 'modules/lookup/stores';
import { BaseStepViewStore } from 'core/stores';
import { ChannelStore } from 'modules/channel/stores';
import {
	notificationError,
	formatDate,
	syncDateWithOriginalTime,
	isSuperAdminOrganization,
	isShippingStateAvailable,
	isSome,
} from 'core/utils';
import { CarrierServiceStore, CarrierStore } from 'modules/carrier/stores';
import { localStorageHandler } from 'core/services';
import { ProductStore } from 'modules/product/stores';
import moment from 'moment';
import _ from 'lodash';
import { AddressAutofillViewStore, BaasicDropdownViewStore } from 'common/stores';
import { AddressValidationTemplate } from 'themes/modules/order/pages';
import { MarketplaceOrderEditForm } from 'modules/order/forms/wizard';
import {
	MarketplaceOrderEditOrderStep,
	MarketplaceOrderEditOrderLinesStep,
	MarketplaceOrderEditShippingStep,
	MarketplaceOrderEditAssignmentStep,
} from 'modules/order/pages/wizard';
import { MarketplaceOrderEditDocumentStep } from 'modules/order/pages/wizard/MarketplaceOrderEditSteps';

class MarketplaceOrderEditViewStore extends BaseStepViewStore {
	@observable useEnteredAddress = true;
	isEdit = false;
	order = null;
	orderDateWithTime = null;
	paymentDateWithTime = null;
	addressValidationResponse = null;
	form = new MarketplaceOrderEditForm({
		onSuccess: async () => {
			await this.createOrUpdateOrder();
		},
		onError: () => {
			this.checkValidity();
			if (this.channelDropdownStore.options.length === 0) {
				this.rootStore.notificationStore.error('MARKETPLACEORDEREDITFORM.NOTIFICATION.MISSINGMANUALORDERCHANNEL');
			}
			this.rootStore.notificationStore.error('MARKETPLACEORDEREDITFORM.NOTIFICATION.INVALIDFORM');
		},
	});

	constructor(rootStore) {
		super(rootStore, [
			{
				name: 'orderStep',
				title: 'MARKETPLACEORDEREDITFORM.TITLE.ORDERSTEP',
				component: MarketplaceOrderEditOrderStep,
				isValid: true,
				order: 1,
			},
			{
				name: 'shippingStep',
				title: 'MARKETPLACEORDEREDITFORM.TITLE.SHIPPINGSTEP',
				component: MarketplaceOrderEditShippingStep,
				isValid: true,
				order: 2,
			},
			{
				name: 'orderLinesStep',
				title: 'MARKETPLACEORDEREDITFORM.TITLE.ORDERLINESSTEP',
				component: MarketplaceOrderEditOrderLinesStep,
				isValid: true,
				order: 3,
			},
			{
				name: 'assignmentStep',
				title: 'MARKETPLACEORDEREDITFORM.TITLE.ASSIGNMENTSTEP',
				component: MarketplaceOrderEditAssignmentStep,
				isValid: true,
				order: 4,
			},
			// {
			// 	name: 'documentStep',
			// 	title: 'MARKETPLACEORDEREDITFORM.TITLE.DOCUMENTSTEP',
			// 	component: MarketplaceOrderEditDocumentStep,
			// 	isValid: true,
			// 	order: 5,
			// },
		]);
		makeObservable(this);
		this.orderStore = new OrderStore(rootStore);
		this.applicationStore = new ApplicationStore(rootStore);
		this.shipmentDocumentStore = new ShipmentDocumentStore(rootStore);
		this.isEdit = rootStore.routerStore.routerState.params.id != null ? true : false;
		this.isSuperAdmin = isSuperAdminOrganization(rootStore);
		this.initialize();
	}

	@action.bound
	initialize = async () => {
		this.suspend();
		this.setDatePickerFormat();
		if (this.isEdit) {
			await this.getOrder(this.rootStore.routerStore.routerState.params.id, {
				embed: this.getOrderEmbed,
			});
		}
		await this.initializeDropdowns();
		await this.initializeOrderLines();
		this.addressAutofillViewStore = new AddressAutofillViewStore(this.rootStore, {
			parentViewData: {
				buyerName: this.form.$('shippingStep.buyerName'),
				address1: this.form.$('shippingStep.shippingAddress1'),
				address2: this.form.$('shippingStep.shippingAddress2'),
				city: this.form.$('shippingStep.shippingCity'),
				zipCode: this.form.$('shippingStep.shippingZipCode'),
				isBaasicDropdown: true,
				stateDropdownStore: this.stateDropdownStore,
				countryDropdownStore: this.countryDropdownStore,
			},
		});
		if (this.sellerDropdownStore.value) this.setChannelRules();
		this.resume();
	};

	@action.bound
	setDatePickerFormat = () => {
		const locale = window.navigator.userLanguage || window.navigator.language;
		moment.locale(locale);
		const localeData = moment.localeData();
		this.datePickerFormat = localeData.longDateFormat('L');
	};

	getSeller = async () => {
		if (this.isEdit) return _.get(this.order, 'seller');

		return this.isSuperAdmin
			? localStorageHandler.get('selectedSeller')
			: await this.applicationStore.getOrganizationInfo();
	};

	@action.bound
	initializeDropdowns = async () => {
		_.each(this.dropdownStores, dropdownStore => {
			this[dropdownStore] = new BaasicDropdownViewStore();
		});

		this.sellerDropdownStore = new BaasicDropdownViewStore();
		await this.sellerDropdownStore.initialize({
			field: this.form.$('orderStep.sellerId'),
			value: await this.getSeller(),
			store: new ApplicationStore(this.rootStore),
			itemTemplate: {
				value: 'id',
				label: 'applicationTitle',
			},
			filterProperties: {
				sellerOnly: true,
			},
			onChangeCallback: this.clearChannelAndSkuData,
		});
		this.form.$('orderStep.sellerId').set('rules', 'required');

		await Promise.all([
			this.orderStatusDropdownStore.initialize({
				field: this.form.$('orderStep.orderStatusId'),
				value: _.get(this.order, 'orderStatus'),
				store: new LookupStore(this.rootStore),
				lookupRoute: lookupRouteName.orderStatus,
				defaultInitialValue: { path: 'extra.abrv', value: orderStatus.unshipped },
			}),
			this.orderTypeDropdownStore.initialize({
				field: this.form.$('orderStep.orderTypeId'),
				value: _.get(this.order, 'orderType'),
				store: new LookupStore(this.rootStore),
				lookupRoute: lookupRouteName.orderType,
				defaultInitialValue: { path: 'extra.abrv', value: 'standardorder' },
			}),
			this.channelDropdownStore.initialize({
				field: this.form.$('orderStep.channelId'),
				value: _.get(this.order, 'channel'),
				store: new ChannelStore(this.rootStore),
				fetchFunc: 'findChannel',
				filterProperties: {
					sellerIds: [_.get(this.sellerDropdownStore, 'value.value')],
					manualOrdersChannelOnly: true,
				},
				fetchInitialValues: this.isSuperAdmin ? this.sellerDropdownStore.value != null : true,
				initCallback: this.checkAndSetChannelValue,
				onChangeCallback: async () => {
					this.requiredCarrierDropdownStore.clear();
					await this.requiredCarrierDropdownStore.updateFilter({
						rpp: 500,
						channelIds: this.form.$('orderStep.channelId').value ? [this.form.$('orderStep.channelId').value] : [],
					});
				},
			}),
			this.confirmationTypeDropdownStore.initialize({
				field: this.form.$('shippingStep.confirmationTypeId'),
				value: _.get(this.order, 'confirmationType'),
				store: new LookupStore(this.rootStore),
				lookupRoute: lookupRouteName.confirmationType,
			}),
			this.currencyDropdownStore.initialize({
				field: this.form.$('shippingStep.currencyId'),
				itemTemplate: {
					value: 'id',
					label: 'currency',
				},
				value: _.get(this.order, 'coreCurrency'),
				store: new LookupStore(this.rootStore),
				lookupRoute: lookupRouteName.currency,
				defaultInitialValue: { path: 'extra.code', value: 'USD' },
			}),
			this.countryDropdownStore.initialize({
				field: this.form.$('shippingStep.shippingCoreCountryId'),
				value: _.get(this.order, 'shippingCoreCountry'),
				store: new LookupStore(this.rootStore),
				lookupRoute: lookupRouteName.country,
				fetchAll: true,
				filterProperties: {
					rpp: 500,
				},
				onChangeCallback: this.updateShippingStateRules,
				defaultInitialValue: { path: 'extra.abrv', value: 'US' },
			}),
			this.stateDropdownStore.initialize({
				field: this.form.$('shippingStep.shippingCoreCountryStateId'),
				value: _.get(this.order, 'shippingCoreCountryState'),
				store: new LookupStore(this.rootStore),
				lookupRoute: lookupRouteName.state,
			}),
			this.requiredWarehouseDropdownStore.initialize({
				field: this.form.$('assignmentStep.requiredWarehouse'),
				store: new ApplicationStore(this.rootStore),
				itemTemplate: {
					value: 'id',
					label: 'applicationTitle',
				},
				filterProperties: {
					rpp: 500,
					warehouseOnly: true,
					isOrganizationActive: true,
					includeOnlySellerWarehouses: true,
					sellerWarehousesOnlyOrganizationId: _.get(this.sellerDropdownStore, 'value.value'),
				},
				defaultInitialValue: { path: 'label', value: _.get(this.order, 'requiredWarehouse') },
			}),
			this.requiredCarrierServiceDropdownStore.initialize({
				field: this.form.$('assignmentStep.requiredCarrierService'),
				store: new CarrierServiceStore(this.rootStore),
				itemTemplate: {
					value: 'id',
					label: 'name',
				},
				filterProperties: {
					rpp: 500,
				},
				defaultInitialValue: { path: 'label', value: _.get(this.order, 'requiredCarrierService') },
			}),
			this.requiredCarrierDropdownStore.initialize({
				field: this.form.$('assignmentStep.requiredCarrierId'),
				store: new CarrierStore(this.rootStore),
				itemTemplate: {
					value: 'id',
					label: 'name',
				},
				filterProperties: {
					rpp: 500,
					channelIds: this.form.$('orderStep.channelId').value ? [this.form.$('orderStep.channelId').value] : [],
				},
				fetchInitialValues: this.form.$('orderStep.channelId').value ? true : false,
				fetchAll: true,
				defaultInitialValue: { path: 'value', value: _.get(this.order, 'requiredCarrierId') },
			}),
			// this.shipmentDocumentTypeDropdownStore.initialize({
			// 	field: this.form.$('documentStep.fileType'),
			// 	staticOptions: [
			// 		{ label: 'Packing Slip', value: shipmentDocumentTypeEnum.packingSlip },
			// 		{ label: 'Bill of Lading', value: shipmentDocumentTypeEnum.billOfLading },
			// 	],
			// }),
		]);
	};

	@action.bound
	checkAndSetChannelValue = () => {
		if (!this.isEdit && this.channelDropdownStore.options.length === 1) {
			this.channelDropdownStore.setValue(this.channelDropdownStore.options[0], true);
		}
	};

	@action.bound
	initializeOrderLines = async () => {
		if (this.isEdit) {
			_.each(this.order.orderLines, async (orderLine, idx) => {
				const field = this.form.$(`orderLinesStep.orderLines[${idx}]`);
				field.productDropdownStore = new BaasicDropdownViewStore();
				await field.productDropdownStore.initialize({
					...this.productDropdownStoreProps(field, orderLine),
				});
				field.orderLineStatusDropdownStore = new BaasicDropdownViewStore();
				await field.orderLineStatusDropdownStore.initialize({
					field: field.$('orderLineStatusId'),
					value: orderLine.orderLineStatus,
					store: new LookupStore(this.rootStore),
					lookupRoute: lookupRouteName.orderLineStatus,
					field: field.$('orderLineStatusId'),
				});
			});
			this.updateOrderLinesRules();
		} else {
			await this.addOrderLine();
		}
	};

	@action.bound
	clearChannelAndSkuData = async sellerDropdownValue => {
		this.channelDropdownStore.clear();
		await this.channelDropdownStore.updateFilter({ sellerIds: [sellerDropdownValue.value] });
		this.setChannelRules();
		this.checkAndSetChannelValue();
		this.requiredWarehouseDropdownStore.clear();
		await this.requiredWarehouseDropdownStore.updateFilter({
			rpp: 500,
			sellerWarehousesOnlyOrganizationId: sellerDropdownValue.value,
		});

		this.form.$('orderLinesStep.orderLines').fields.forEach(async field => {
			field.$('quantity').set('value', '');
			field.productDropdownStore.clear();
			await field.productDropdownStore.updateFilter({ sellerIds: [sellerDropdownValue.value] });
		});
	};

	@action.bound
	setChannelRules = () => {
		if (this.channelDropdownStore.options.length === 0) {
			this.rootStore.notificationStore.error('MARKETPLACEORDEREDITFORM.NOTIFICATION.MISSINGMANUALORDERCHANNEL');
		} else {
			this.form.$('orderStep.channelId').set('rules', this.channelDropdownStore.options.length > 1 ? 'required' : '');
		}
	};

	@action.bound
	getOrder = async (id, options) => {
		this.order = await this.orderStore.getOrder(id, options);
		this.setDates();
		this.form.updateForm(this.order, true);
	};

	@action.bound
	setDates = () => {
		this.orderDateWithTime = formatDate(this.order.orderDate, false); // initial date, received in UTC
		this.order.orderDate = formatDate(this.order.orderDate, false, this.datePickerFormat); // to get the same date as on ListView

		this.paymentDateWithTime = formatDate(this.order.paymentDate, false);
		this.order.paymentDate = formatDate(this.order.paymentDate, false, this.datePickerFormat);

		this.order.shipByDate = formatDate(this.order.shipByDate, false, this.datePickerFormat);

		if (this.order.deliverByDate != null) {
			this.order.deliverByDate = formatDate(this.order.deliverByDate, false, this.datePickerFormat);
		}
	};

	@action.bound
	addOrderLine = async () => {
		const field = this.form.$('orderLinesStep.orderLines').add({
			product: null,
			quantity: '',
			id: '',
			unitPrice: '',
			taxPaid: '',
			orderLineStatus: null,
		});
		field.productDropdownStore = new BaasicDropdownViewStore();
		field.orderLineStatusDropdownStore = new BaasicDropdownViewStore();

		await Promise.all([
			field.productDropdownStore.initialize({
				...this.productDropdownStoreProps(field),
			}),
			field.orderLineStatusDropdownStore.initialize({
				field: field.$('orderLineStatusId'),
				store: new LookupStore(this.rootStore),
				lookupRoute: lookupRouteName.orderLineStatus,
				defaultInitialValue: { path: 'extra.abrv', value: orderLineStatuses.entered },
			}),
		]);
	};

	@action.bound
	updateShippingStateRules = () => {
		if (!isShippingStateAvailable(_.get(this.countryDropdownStore, 'value.extra.countryCode'))) {
			this.stateDropdownStore.clear();
			this.form.$('shippingStep.shippingCoreCountryStateId').set('rules', '');
			this.form.$('shippingStep.shippingCoreCountryStateId').validate();
		} else {
			this.form.$('shippingStep.shippingCoreCountryStateId').set('rules', 'required');
		}
	};

	@action.bound
	updateOrderLinesRules = () => {
		this.form.$('orderLinesStep.orderLines').fields.forEach(async field => {
			if (!field.productDropdownStore.value) return;
			if (field.productDropdownStore.value.extra.isShippable) {
				field.$('quantity').set('rules', 'required|numeric|numericMinValue|notZero|integer');
				field.$('orderLineStatusId').set('rules', 'required');
			} else {
				field.$('quantity').set('rules', '');
				field.$('orderLineStatusId').set('rules', '');
				this.form.$('assignmentStep').reset();
			}
			field.validate();
		});
	};

	@action.bound
	isAddressValid = async () => {
		this.suspend();
		if (!this.addressNeedsValidation()) {
			return true;
		}

		this.addressValidationResponse = await this.orderStore.validateOrderAddress(this.initializeAddressValidationData());
		this.resume();

		if (typeof _.get(this.addressValidationResponse, 'isResidential') === 'boolean') {
			this.isResidential = this.addressValidationResponse.isResidential;
		}
		if (this.addressValidationResponse.updatedAddress == null) return true;

		this.openModal({
			data: this,
			component: AddressValidationTemplate,
			onClose: this.resetAddressValidationData,
		});
	};

	@computed get everyProductIsNonShippable() {
		const products = this.form.$('orderLinesStep.orderLines').map(ol => ol.productDropdownStore.value);
		return products.every(product => product && !product.extra.isShippable);
	}

	@action.bound
	createOrUpdateOrder = async () => {
		try {
			if (await this.isAddressValid()) {
				this.suspend();
				const order = this.initializeOrder();
				if (this.isEdit) {
					order.orderDate = moment(order.orderDate, this.datePickerFormat).format('YYYY-MM-DDTHH:mm:ss');
					order.paymentDate = moment(order.paymentDate, this.datePickerFormat).format('YYYY-MM-DDTHH:mm:ss');
					order.deliverByDate = order.deliverByDate
						? moment(order.deliverByDate, this.datePickerFormat).format('YYYY-MM-DDTHH:mm:ss')
						: null;
					order.shipByDate = order.shipByDate
						? moment(order.shipByDate, this.datePickerFormat).format('YYYY-MM-DDTHH:mm:ss')
						: null;
					order.addressValidationResponse = !this.useEnteredAddress ? null : this.addressValidationResponse;
					await this.orderStore.updateOrder(order, sourceOfChange.updateOrder);
					this.rootStore.notificationStore.success([
						'MARKETPLACEORDEREDITFORM.NOTIFICATION.UPDATE',
						{ orderNumber: order.orderNumber },
					]);
				} else {
					order.orderDate = moment(order.orderDate, this.datePickerFormat).format('YYYY-MM-DDTHH:mm:ss');
					order.paymentDate = moment(order.paymentDate, this.datePickerFormat).format('YYYY-MM-DDTHH:mm:ss');
					order.deliverByDate = order.deliverByDate
						? moment(order.deliverByDate, this.datePickerFormat).format('YYYY-MM-DDTHH:mm:ss')
						: null;
					order.shipByDate = moment(order.shipByDate, this.datePickerFormat).format('YYYY-MM-DDTHH:mm:ss');
					order.addressValidationResponse = this.addressValidationResponse;
					await this.orderStore.createOrder(order, sourceOfChange.createOrder);
					this.rootStore.notificationStore.success([
						'MARKETPLACEORDEREDITFORM.NOTIFICATION.CREATE',
						{ orderNumber: order.orderNumber },
					]);
				}
				await this.raiseWarningIfOrderIsDisabled(order);
				this.rootStore.routerStore.goTo('master.app.main.order.marketplace.list');
			}
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
			this.resume();
		}
	};

	raiseWarningIfOrderIsDisabled = async order => {
		if (order.sellerId) {
			await this.applicationStore.getApplication(order.sellerId, {
				embed: ['sellerOrganizationInfo'],
			});

			if (
				!this.applicationStore.item.sellerOrganizationInfo.partiallyFulfillMixedOrders &&
				order.orderLines.some(ol => !ol.isProductActive)
			) {
				this.rootStore.notificationStore.warning([
					'ORDERVIEW.PARTIALLYFULFILLMIXEDORDERS',
					{ orderId: order.orderNumber },
				]);
			}
		}
	};

	initializeOrder = () => {
		const { orderStep, shippingStep, assignmentStep } = this.form.values();
		if (!this.useEnteredAddress) {
			this.applyUpdatedAddressData(shippingStep);
		}

		let order = {
			...orderStep,
			...shippingStep,
			requiredCarrierService: _.get(this.requiredCarrierServiceDropdownStore, 'value.label'),
			requiredWarehouse: _.get(this.requiredWarehouseDropdownStore, 'value.label'),
			requiredCarrierId: assignmentStep.requiredCarrierId,
			addressChanged: this.addressValidationResponse ? !this.useEnteredAddress : _.get(this.order, 'addressChanged'),
			isResidential: this.addressValidationResponse ? this.isResidential : _.get(this.order, 'isResidential'),
			isAddressVerified: this.addressValidationResponse
				? this.addressValidationResponse.isAddressVerified
				: _.get(this.order, 'isAddressVerified'),
			orderLines: this.form.$('orderLinesStep.orderLines').map(orderLine => {
				return {
					id: orderLine.value.id,
					sku: orderLine.productDropdownStore.value.extra.mainSku,
					isProductActive: orderLine.productDropdownStore.value.extra.isActive,
					productId: orderLine.value.productId,
					quantity: orderLine.value.quantity ? orderLine.value.quantity : 1,
					unitPrice: parseFloat(orderLine.value.unitPrice),
					taxPaid: parseFloat(orderLine.value.taxPaid),
					orderLineStatusId: orderLine.value.orderLineStatusId,
					sellerId: orderLine.value.sellerId,
					orderItemId: orderLine.value.orderItemId,
					amazonBuyShippingItemId: orderLine.value.amazonBuyShippingItemId,
				};
			}),
		};

		return order;
	};

	applyUpdatedAddressData = shippingStep => {
		shippingStep.shippingAddress1 = this.addressValidationResponse.updatedAddress.streetLines[0]
			? this.addressValidationResponse.updatedAddress.streetLines[0]
			: '';
		shippingStep.shippingAddress2 = this.addressValidationResponse.updatedAddress.streetLines[1]
			? this.addressValidationResponse.updatedAddress.streetLines[1]
			: '';
		shippingStep.shippingCity = this.addressValidationResponse.updatedAddress.city;
		shippingStep.shippingZipCode = this.addressValidationResponse.updatedAddress.postalCode;
	};

	@action.bound
	async onValidateAddressBack() {
		this.rootStore.viewStore.selectedModal.close();
	}

	resetAddressValidationData = () => {
		this.addressValidationResponse.isAddressVerified = false;
		this.useEnteredAddress = true;
	};

	@action.bound
	onToggleAddress(useEnteredAddress) {
		this.useEnteredAddress = useEnteredAddress;
	}

	@action.bound
	async onValidateAddressConfirm() {
		this.rootStore.viewStore.selectedModal.success();
		await this.createOrUpdateOrder();
	}

	@action.bound
	addressNeedsValidation() {
		if (_.get(this.addressValidationResponse, 'isAddressVerified') === true) return false;
		if (!this.isEdit) return true;

		const { shippingStep } = this.form.values();

		return (
			this.order.shippingAddress1 !== shippingStep.shippingAddress1 ||
			this.order.shippingAddress2 !== shippingStep.shippingAddress2 ||
			this.order.shippingCity !== shippingStep.shippingCity ||
			this.order.shippingZipCode !== shippingStep.shippingZipCode ||
			this.order.shippingCoreCountry.id !== shippingStep.shippingCoreCountryId ||
			(this.order.shippingCoreCountryState ? this.order.shippingCoreCountryState.id : null) !==
				shippingStep.shippingCoreCountryStateId
		);
	}

	@action.bound
	initializeAddressValidationData() {
		const { shippingStep, orderStep } = this.form.values();

		return {
			id: this.isEdit ? this.order.id : undefined,
			shippingAddress1: shippingStep.shippingAddress1,
			shippingAddress2: shippingStep.shippingAddress2,
			shippingCity: shippingStep.shippingCity,
			shippingZipCode: shippingStep.shippingZipCode,
			buyerPhone: shippingStep.buyerPhone,
			buyerName: shippingStep.buyerName,
			shippingCoreCountryId: shippingStep.shippingCoreCountryId,
			shippingCoreCountryStateId: shippingStep.shippingCoreCountryStateId,
			shipByDate: moment(orderStep.shipByDate, this.datePickerFormat).toDate(),
			dateFormat: this.datePickerFormat.toLowerCase().replaceAll('m', 'M'),
		};
	}

	@action.bound
	uploadDocument = async filesMeta => {
		try {
			if (filesMeta && filesMeta.length == 1) {
				const { orderStep } = this.form.values();
				const formData = new FormData();
				formData.append(`packingSlip-${orderStep.id}`, filesMeta[0]);
				await this.shipmentDocumentStore.upload(formData);
			}
		} catch (error) {}
	};

	onOrderLineDelete = async (e, field) => {
		this.form.onDeleteArrayField(e, field, this.form.$('orderLinesStep.orderLines'));
		await this.reloadProducts();
	};

	reloadProducts = async () => {
		this.form.$('orderLinesStep.orderLines').fields.forEach(async field => {
			await field.productDropdownStore.updateFilter({
				productIdsToExclude: this.form.$('orderLinesStep.orderLines').value.map(ol => ol.productId),
			});
		});
	};

	getOrderEmbed = [
		'channel',
		'confirmationType',
		'coreCurrency',
		'orderType',
		'shippingCoreCountryState',
		'shippingCoreCountry',
		'seller',
		'seller.sellerOrganizationInfo',
		'orderLines',
		'orderLines.product',
		'orderLines.orderLineStatus',
		'orderStatus',
	];

	dropdownStores = [
		'orderStatusDropdownStore',
		'orderTypeDropdownStore',
		'confirmationTypeDropdownStore',
		'currencyDropdownStore',
		'countryDropdownStore',
		'productCategoryDropdownStore',
		'stateDropdownStore',
		'orderLineStatusDropdownStore',
		'channelDropdownStore',
		'requiredWarehouseDropdownStore',
		'requiredCarrierServiceDropdownStore',
		'requiredCarrierDropdownStore',
		//'shipmentDocumentTypeDropdownStore',
	];

	productDropdownStoreProps = (field, orderLine) => {
		return {
			field: field.$('productId'),
			value: orderLine ? orderLine.product : null,
			store: new ProductStore(this.rootStore),
			itemTemplate: {
				value: 'id',
				label: 'mainSku',
			},
			filterProperties: {
				productIdsToExclude: this.form.$('orderLinesStep.orderLines').value.map(ol => ol.productId),
				sellerIds: [this.form.$('orderStep.sellerId').value],
				channelIds:
					this.channelDropdownStore && this.channelDropdownStore.value
						? [this.channelDropdownStore.value.value]
						: undefined,
			},
			onChangeCallback: value => {
				this.reloadProducts();
				if (value) this.updateOrderLinesRules();
			},
		};
	};
}

export default MarketplaceOrderEditViewStore;
